import React, { useMemo } from "react";
import Big from "big.js";

import { DataTable, useDataTable, createColumnHelper } from "@puzzle/ui";
import { sumAmounts, formatMoney, parseAbsolute } from "@puzzle/utils";

import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { useCompanyDateFormatter } from "components/companies/useCompanyDateFormatter";

import { PayrollLinkedTransactionFragment } from "./graphql.generated";

import { vars } from "@puzzle/theme";

const columnHelper = createColumnHelper<PayrollLinkedTransactionFragment & { isTotal?: boolean }>();

export const RelatedTransactionsTable = ({
  transactions,
}: {
  transactions: PayrollLinkedTransactionFragment[];
}) => {
  const { timeZone } = useActiveCompany<true>();
  const totalAmount = useMemo(
    () => Big(sumAmounts(transactions.map(({ amount }) => amount ?? "0"))).abs(),
    [transactions]
  );
  const dateFormatter = useCompanyDateFormatter({
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("date", {
        header: "Date",
        size: 80,
        cell: ({ getValue }) => dateFormatter.format(parseAbsolute(getValue(), timeZone)),
      }),
      columnHelper.accessor("description", {
        header: "Description",
        footer: () => <>Total transactions</>,
      }),
      columnHelper.accessor("amount", {
        header: "Amount",
        size: 80,
        meta: {
          align: "right",
        },
        cell: ({ getValue }) => {
          const amount = Big(getValue() ?? "0").abs();
          return !amount.eq(0) ? formatMoney({ currency: "USD", amount }) : "-";
        },
        footer: () => {
          return <>{formatMoney({ currency: "USD", amount: totalAmount })}</>;
        },
      }),
    ];
  }, [dateFormatter, timeZone, totalAmount]);

  const table = useDataTable({
    data: transactions,
    columns,
  });

  return (
    <DataTable
      css={{ "tbody td": { background: vars.colors["surface-neutral-highlight"] } }}
      table={table}
      bordered
      hoverableRows={false}
    />
  );
};
